import type { Ref } from 'vue'

/**
 * 「边框即倒计时」：把一个圆角盒子的描边当成倒计时条，随时间退掉。
 *
 * 这套观感是 Zack 定的（m-booking-progress 那一版「进度条就是边框」），
 * 后来 home-nearby-banner 也用了同一手法。抽出来是因为里面有两个**必须踩过
 * 才知道**的坑，不该存在第二份实现：
 *
 * ⚠️ 坑一：不能用 pathLength="1" 那个偷懒写法 —— 在 <rect> 上浏览器算出来的
 *         stroke-dasharray 会变成 1px。必须写实 x/y/width/height/rx，
 *         再用 getTotalLength() 拿真实周长。
 *
 * ⚠️ 坑二：getComputedStyle 回的是**声明值**（比如 4rem ≈ 38.5px），不是浏览器
 *         实际用的值。药丸高度只有 41px 时 CSS 会把圆角夹到 height/2 ≈ 20.7px，
 *         而 SVG 的 <rect> 只夹 ry 不夹 rx —— 直接用声明值会得到一个被拉长的
 *         椭圆角：既不贴盒子的形，周长也算错。实测：
 *             rx = 38.46（声明值）→ getTotalLength 647.5
 *             rx = 20.67（夹过）  → getTotalLength 658.1  ← 真实周长 658.9
 *         差 11px，表现是「倒计时走完了边框还剩一小截」。所以必须自己夹。
 */
export const useCountdownRing = (
  boxRef: Ref<HTMLElement | null>,
  ringRef: Ref<SVGRectElement | null>,
  strokeWidth = 2,
) => {
  /** 量几何、铺虚线，返回周长；量不到回 0 */
  const measure = () => {
    const box = boxRef.value
    const rect = ringRef.value
    if (!box || !rect) return 0
    const { width, height } = box.getBoundingClientRect()
    if (!width || !height) return 0

    const radius = Math.min(
      parseFloat(getComputedStyle(box).borderTopLeftRadius) || 0,
      width / 2,
      height / 2,
    )
    const inset = strokeWidth / 2
    rect.setAttribute('x', String(inset))
    rect.setAttribute('y', String(inset))
    rect.setAttribute('width', String(Math.max(width - strokeWidth, 0)))
    rect.setAttribute('height', String(Math.max(height - strokeWidth, 0)))
    rect.setAttribute('rx', String(Math.max(radius - inset, 0)))

    // 属性写完才量得准
    const len = rect.getTotalLength?.() || 0
    if (!len) return 0
    rect.style.strokeDasharray = String(len)
    rect.style.strokeDashoffset = '0'
    rect.style.visibility = 'visible'
    return len
  }

  /**
   * 起倒计时。返回可 kill 的 tween（量不到几何时返回一个纯计时的
   * delayedCall —— **绝不能因为边框没画出来就永远不触发 onDone**）。
   */
  const start = (gsap: any, seconds: number, onDone: () => void) => {
    const len = measure()
    if (len && ringRef.value) {
      return gsap.to(ringRef.value, {
        strokeDashoffset: len,
        duration: seconds,
        // 线性、无回弹 —— 匀速缓变不触发前庭不适，reduced-motion 下也能留着
        ease: 'none',
        onComplete: onDone,
      })
    }
    return gsap.delayedCall(seconds, onDone)
  }

  return { measure, start }
}
